import { formatOfContentType, type MaterialFormat } from './materials';

/**
 * How a material's small facts are written: its kind, its length, its size.
 *
 * Ported from Project 180's `apps/project180/web/src/lib/fileFacts.ts`, narrowed to the six
 * formats Collab takes. The words are Project 180's on purpose — "Slides", "Document",
 * "Picture" — so a teacher who uses both reads one vocabulary. Pure.
 */

export type FileKind = 'pdf' | 'slides' | 'document' | 'picture' | 'text' | 'file';

const KIND_OF_FORMAT: Readonly<Record<MaterialFormat, FileKind>> = {
  pdf: 'pdf',
  pptx: 'slides',
  docx: 'document',
  png: 'picture',
  jpeg: 'picture',
  text: 'text',
};

/** What a kind is called on screen. */
export const KIND_LABEL: Readonly<Record<FileKind, string>> = {
  pdf: 'PDF',
  slides: 'Slides',
  document: 'Document',
  picture: 'Picture',
  text: 'Text',
  file: 'File',
};

/** The extension on the icon's band. */
export const KIND_EXTENSION: Readonly<Record<MaterialFormat, string>> = {
  pdf: 'pdf', pptx: 'pptx', docx: 'docx', png: 'png', jpeg: 'jpg', text: 'txt',
};

/** A stored content type's kind. Anything the upload would not take is just a file. */
export function fileKindOf(contentType: string): FileKind {
  const format = formatOfContentType(contentType);
  return format === null ? 'file' : KIND_OF_FORMAT[format];
}

/** The icon's band text, or "file". */
export function extensionOf(contentType: string): string {
  const format = formatOfContentType(contentType);
  return format === null ? 'file' : KIND_EXTENSION[format];
}

/**
 * "340 KB", "2.4 MB", "10.0 MB" — the one way a size is written, on every screen. MB keep one
 * decimal at every size, as the upload panel and the material page always showed. Never
 * "0 KB" for something that is not empty, and never NaN.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const PAGED: ReadonlySet<FileKind> = new Set(['pdf', 'slides', 'document']);

/** "12 slides", "1 page", or nothing when nobody has counted yet. */
export function pagesLabel(kind: FileKind, pages: number | null | undefined): string | null {
  if (!pages || !PAGED.has(kind)) return null;
  const noun = kind === 'slides' ? 'slide' : 'page';
  return `${pages} ${noun}${pages === 1 ? '' : 's'}`;
}

export interface FactsInput {
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly pageCount?: number | null;
}

/** The small line under a material's title: its kind, its length, its size. */
export function fileFacts(material: FactsInput): string {
  const kind = fileKindOf(material.contentType);
  return [KIND_LABEL[kind], pagesLabel(kind, material.pageCount ?? null), formatBytes(material.sizeBytes)]
    .filter(Boolean)
    .join(' · ');
}
